<template>
  <div class="matriz-wrapper">

    <!-- Legend -->
    <div class="matriz-legend">
      <Icon name="matrix" :size="14" color="var(--primary)" />
      <span class="matriz-legend__bold">Modo matriz:</span>
      <span class="matriz-legend__text">captura cantidades de varios comedores al mismo tiempo. Desactiva un comedor con el checkbox de su columna.</span>
    </div>

    <!-- Table wrapper -->
    <div class="matriz-scroll">
      <div :style="{ minWidth: tableMinWidth + 'px' }">

        <!-- Header row: comedor names -->
        <div class="matriz-head" :style="{ gridTemplateColumns: gridTemplate }">

          <div class="matriz-head__product-col">Producto</div>

          <button
            v-for="cid in activeIds"
            :key="cid"
            @click="$emit('toggle-muted', cid)"
            class="comedor-col-btn"
            :class="{ 'comedor-col-btn--muted': mutedIds.includes(cid) }"
          >
            <div
              class="comedor-checkbox"
              :class="{ 'comedor-checkbox--muted': mutedIds.includes(cid) }"
            >
              <Icon v-if="!mutedIds.includes(cid)" name="check" :size="10" color="#fff" :stroke="3" />
            </div>
            <span
              class="comedor-col-btn__name"
              :class="{ 'comedor-col-btn__name--muted': mutedIds.includes(cid) }"
            >{{ getComedorById(cid)?.name }}</span>
          </button>

          <div class="matriz-sigma-col">Sigma Total</div>
        </div>

        <!-- Subtotal row -->
        <div class="matriz-subtotals" :style="{ gridTemplateColumns: gridTemplate }">
          <div class="matriz-subtotals__label">Subtotal por comedor</div>

          <div
            v-for="cid in activeIds"
            :key="cid"
            class="col-subtotal"
            :class="{ 'col-subtotal--active': !mutedIds.includes(cid) && colTotal(cid) > 0 }"
          >
            {{ mutedIds.includes(cid) ? '—' : (colTotal(cid) > 0 ? money(colTotal(cid)) : '—') }}
          </div>

          <div class="matriz-sigma-subtotal">
            {{ money(visibleCols.reduce((s, cid) => s + colTotal(cid), 0)) }}
          </div>
        </div>

        <!-- Product rows -->
        <div
          v-for="p in filteredProducts"
          :key="p.id"
          v-memo="[activeIds.map(cid => (mutedIds.includes(cid) ? 'm' : '') + (localQty[key(cid, p.id)] || 0) + '_' + lastQty(cid, p.id)).join(',')]"
          class="matriz-row"
          :class="{ 'matriz-row--active': rowQty(p.id) > 0 }"
          :style="{ gridTemplateColumns: gridTemplate }"
        >
          <!-- Product info cell -->
          <div
            class="matriz-product-cell"
            :class="{ 'matriz-product-cell--active': rowQty(p.id) > 0 }"
          >
            <div class="matriz-product-cell__name">{{ p.name }}</div>
            <div class="matriz-product-cell__meta">
              <span>{{ p.unit }}</span>
              <span class="matriz-product-cell__price">{{ money(p.price) }}</span>
            </div>
          </div>

          <!-- Qty input cells -->
          <div
            v-for="cid in activeIds"
            :key="cid"
            class="matrix-qty-cell"
            :class="{ 'matrix-qty-cell--muted': mutedIds.includes(cid) }"
          >
            <!-- Last-order hint -->
            <span
              v-if="lastQty(cid, p.id)"
              :title="`Último pedido: ${lastQty(cid, p.id)}`"
              class="hint-badge hint-badge--abs"
            >{{ lastQty(cid, p.id) }}</span>

            <input
              type="number"
              :inputmode="p.decimal ? 'decimal' : 'numeric'"
              min="0"
              :step="p.decimal ? '0.5' : '1'"
              :value="localQty[key(cid, p.id)] || ''"
              :placeholder="p.decimal ? '1.0' : '0'"
              class="cell-input"
              :class="{ 'cell-input--active': !!localQty[key(cid, p.id)] }"
              @change="e => handleChange(cid, p.id, e, p.decimal)"
              @wheel.prevent="e => handleWheel(cid, p.id, e, p.decimal)"
              @focus="e => e.target.select()"
            />
          </div>

          <!-- Row total cell -->
          <div
            class="row-total-cell"
            :class="{ 'row-total-cell--active': rowTotal(p.id) > 0 }"
          >
            <div
              class="row-total-cell__amount"
              :class="{ 'row-total-cell__amount--active': rowTotal(p.id) > 0 }"
            >{{ rowTotal(p.id) > 0 ? money(rowTotal(p.id)) : '—' }}</div>
            <div v-if="rowQty(p.id) > 0" class="row-total-cell__units">{{ fmtCount(rowQty(p.id)) }} u</div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch, onMounted } from 'vue'
import Icon from '@/components/ui/Icon.vue'

const props = defineProps({
  products:  { type: Array,  default: () => [] },
  activeIds: { type: Array,  default: () => [] },
  mutedIds:  { type: Array,  default: () => [] },
  cart:      { type: Object, default: () => ({}) },
  category:  { type: String, default: 'all' },
  search:    { type: String, default: '' },
  store:     { type: Object, default: null },
})

const emit = defineEmits(['set-qty', 'toggle-muted'])

// ── Local qty state for instant display ──────────────────────────────────────
const localQty = reactive({})

function key(cid, pid) { return `${cid}__${pid}` }

watch(() => props.cart, (cart) => {
  const seen = new Set()
  for (const [cid, prods] of Object.entries(cart)) {
    for (const [pid, qty] of Object.entries(prods)) {
      const k = key(cid, pid)
      localQty[k] = qty
      seen.add(k)
    }
  }
  for (const k of Object.keys(localQty)) {
    if (!seen.has(k)) delete localQty[k]
  }
}, { immediate: true, deep: true })

// ── Wheel handler ─────────────────────────────────────────────────────────────
const _pending = new Map()
let _raf = null

function handleWheel(cid, pid, e, decimal = false) {
  const k    = key(cid, pid)
  const step = decimal ? 0.5 : 1
  const cur  = localQty[k] || 0
  let next   = Math.max(0, cur + (e.deltaY < 0 ? step : -step))
  if (decimal) next = Math.round(next * 10) / 10
  if (next === cur) return

  if (next > 0) localQty[k] = next
  else          delete localQty[k]

  _pending.set(k, { cid, pid, qty: next })
  if (!_raf) {
    _raf = requestAnimationFrame(() => {
      for (const { cid, pid, qty } of _pending.values()) {
        emit('set-qty', { comedorId: cid, productId: pid, qty })
      }
      _pending.clear()
      _raf = null
    })
  }
}

function handleChange(cid, pid, e, decimal = false) {
  const raw = e.target.value
  const qty = raw === '' ? 0 : Math.max(0, decimal ? (parseFloat(raw) || 0) : (parseInt(raw) || 0))
  const k   = key(cid, pid)
  if (qty > 0) localQty[k] = qty
  else         delete localQty[k]
  emit('set-qty', { comedorId: cid, productId: pid, qty })
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function money(n) {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtCount(n) { return n.toLocaleString('es-MX') }

const productColW = 280
const totalColW   = 130

const visibleCols = computed(() => props.activeIds.filter(id => !props.mutedIds.includes(id)))

const cellW = computed(() =>
  Math.max(155, Math.min(200, Math.floor(620 / Math.max(1, visibleCols.value.length))))
)

const tableMinWidth = computed(() =>
  productColW + props.activeIds.length * cellW.value + totalColW
)

const gridTemplate = computed(() =>
  `${productColW}px repeat(${props.activeIds.length}, ${cellW.value}px) ${totalColW}px`
)

const filteredProducts = computed(() => {
  let list = props.products.length ? props.products : (props.store?.products?.value || [])
  if (props.category && props.category !== 'all') {
    list = list.filter(p => p.cat === props.category)
  }
  if (props.search && props.search.trim()) {
    const q = props.search.toLowerCase()
    list = list.filter(p =>
      p.name.toLowerCase().includes(q) || (p.sku && p.sku.toLowerCase().includes(q))
    )
  }
  return list
})

function colTotal(cid) {
  return filteredProducts.value.reduce((s, p) => {
    const q = localQty[key(cid, p.id)] || 0
    return s + q * p.price
  }, 0)
}

function rowQty(pid) {
  return props.activeIds.reduce((s, cid) => {
    if (props.mutedIds.includes(cid)) return s
    return s + (localQty[key(cid, pid)] || 0)
  }, 0)
}

function getComedorById(cid) {
  return props.store?.getCenterById(cid) || null
}

function lastQty(cid, pid) {
  return props.store?.lastOrderItems?.[cid]?.[String(pid)] || 0
}

onMounted(() => {
  props.store?.loadLastOrderItems?.(props.activeIds)
})

function rowTotal(pid) {
  const p = filteredProducts.value.find(x => x.id === pid)
  if (!p) return 0
  return props.activeIds.reduce((s, cid) => {
    if (props.mutedIds.includes(cid)) return s
    return s + (localQty[key(cid, pid)] || 0) * p.price
  }, 0)
}
</script>

<style scoped>
/* ─── Page wrapper ───────────────────────────────────────────────────────────── */
.matriz-wrapper {
  padding: 24px;
  padding-top: 16px;
}

/* ─── Legend ─────────────────────────────────────────────────────────────────── */
.matriz-legend {
  margin-bottom: 12px;
  padding: 10px 14px;
  background: var(--primary-softer);
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--ff-body);
  font-size: 12px;
  color: var(--primary);
}

.matriz-legend__bold {
  font-weight: 700;
}

.matriz-legend__text {
  color: var(--text-2);
}

/* ─── Table wrapper ──────────────────────────────────────────────────────────── */
.matriz-scroll {
  background: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--border);
  overflow-x: auto;
  overflow-y: visible;
}

/* ─── Header row ─────────────────────────────────────────────────────────────── */
.matriz-head {
  display: grid;
  position: sticky;
  top: 0;
  background: var(--surface);
  z-index: 5;
  border-bottom: 1.5px solid var(--border-strong);
}

.matriz-head__product-col {
  padding: 12px 18px;
  font-family: var(--ff-head);
  font-size: 11px;
  color: var(--text-2);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  position: sticky;
  left: 0;
  background: var(--surface);
  z-index: 2;
}

/* ─── Comedor column buttons ─────────────────────────────────────────────────── */
.comedor-col-btn {
  padding: 10px 6px;
  background: none;
  border: none;
  border-left: 1px solid var(--border);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 1;
  transition: opacity 0.15s;
}

.comedor-col-btn--muted {
  opacity: 0.35;
}

.comedor-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 2px solid var(--primary);
  background: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.comedor-checkbox--muted {
  border-color: var(--border-strong);
  background: var(--surface);
}

.comedor-col-btn__name {
  font-family: var(--ff-head);
  font-size: 11px;
  font-weight: 700;
  color: var(--text);
  text-align: center;
  line-height: 1.2;
}

.comedor-col-btn__name--muted {
  text-decoration: line-through;
}

/* ─── Sigma column ───────────────────────────────────────────────────────────── */
.matriz-sigma-col {
  padding: 10px 12px;
  border-left: 2px solid var(--primary);
  background: var(--primary-softer);
  font-family: var(--ff-head);
  font-size: 11px;
  color: var(--primary);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
}

/* ─── Subtotal row ───────────────────────────────────────────────────────────── */
.matriz-subtotals {
  display: grid;
  background: var(--bg);
  border-bottom: 1px solid var(--border-strong);
}

.matriz-subtotals__label {
  padding: 8px 18px;
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-2);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  position: sticky;
  left: 0;
  background: var(--bg);
  z-index: 1;
  display: flex;
  align-items: center;
}

.col-subtotal {
  padding: 8px 6px;
  border-left: 1px solid var(--border);
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 12px;
  color: var(--text-3);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.col-subtotal--active {
  color: var(--text);
}

.matriz-sigma-subtotal {
  padding: 8px 12px;
  border-left: 2px solid var(--primary);
  background: var(--primary-soft);
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--primary);
  text-align: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* ─── Product rows ───────────────────────────────────────────────────────────── */
.matriz-row {
  display: grid;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  transition: background 0.12s;
}

.matriz-row--active {
  background: var(--primary-softer);
}

/* ─── Product info cell ──────────────────────────────────────────────────────── */
.matriz-product-cell {
  padding: 10px 18px;
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--surface);
  border-right: 1px solid var(--border);
  transition: background 0.12s;
}

/* When the row is active, the sticky cell must match the row's background */
.matriz-row--active .matriz-product-cell {
  background: var(--primary-softer);
}

.matriz-product-cell__name {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
  line-height: 1.3;
}

.matriz-product-cell__meta {
  font-family: var(--ff-body);
  font-size: 11px;
  color: var(--text-2);
  margin-top: 2px;
  display: flex;
  gap: 10px;
}

.matriz-product-cell__price {
  color: var(--text);
  font-weight: 700;
}

/* ─── Qty input cells ────────────────────────────────────────────────────────── */
.matrix-qty-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid var(--border);
  opacity: 1;
  pointer-events: auto;
}

.matrix-qty-cell--muted {
  opacity: 0.3;
  pointer-events: none;
}

/* ─── Last-order hint badge ──────────────────────────────────────────────────── */
.hint-badge {
  font-family: var(--ff-head);
  font-size: 14px;
  font-weight: 700;
  color: var(--hint);
  background: rgba(249, 115, 22, 0.08);
  border: 1.5px solid rgba(249, 115, 22, 0.35);
  border-radius: 4px;
  padding: 1px 5px;
  line-height: 1.4;
  white-space: nowrap;
}

.hint-badge--abs {
  position: absolute;
  right: calc(50% + 26px);
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

/* ─── Cell input ─────────────────────────────────────────────────────────────── */
.cell-input {
  width: 44px;
  height: 28px;
  padding: 0 4px;
  text-align: center;
  border: 1.5px solid var(--border-strong);
  background: var(--surface);
  border-radius: 6px;
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 12px;
  color: var(--text-3);
  outline: none;
  box-sizing: border-box;
  cursor: ns-resize;
}

.cell-input--active {
  border-color: var(--primary);
  background: var(--primary-softer);
  color: var(--primary);
}

/* ─── Row total cell ─────────────────────────────────────────────────────────── */
.row-total-cell {
  padding: 10px 12px;
  border-left: 2px solid var(--border);
  background: var(--bg);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
}

.row-total-cell--active {
  border-left-color: var(--primary);
  background: var(--primary-soft);
}

.row-total-cell__amount {
  font-family: var(--ff-head);
  font-weight: 700;
  font-size: 14px;
  color: var(--text-3);
}

.row-total-cell__amount--active {
  color: var(--primary);
}

.row-total-cell__units {
  font-family: var(--ff-body);
  font-size: 10px;
  color: var(--text-2);
  margin-top: 2px;
}

/* ─── Hide spinners ──────────────────────────────────────────────────────────── */
input[type=number]::-webkit-outer-spin-button,
input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number] { -moz-appearance: textfield; }
</style>
